// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"

// Use event delegation at document level for all click events.
// This works regardless of Turbo navigations because the listener is on document,
// and we fetch the modal elements each time a click occurs.

document.addEventListener("click", (event) => {
  // Handle confirmation modal triggers
  const trigger = event.target.closest("[data-confirm-modal]");
  if (trigger) {
    event.preventDefault();

    const modal = document.getElementById("confirm-modal");
    const titleEl = document.getElementById("confirm-title");
    const messageEl = document.getElementById("confirm-message");
    const iconEl = document.getElementById("confirm-icon");
    const submitBtn = document.getElementById("confirm-submit");

    if (!modal || !titleEl || !messageEl || !submitBtn || !iconEl) return;

    // Default delete styling
    const defaults = {
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this? This action cannot be undone.",
      buttonText: "Delete",
      buttonClass: "bg-red-600 hover:bg-red-700",
      iconClass: "bg-red-100",
      iconSvg: `<svg class="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>`
    };

    const config = {
      title: trigger.dataset.confirmTitle || defaults.title,
      message: trigger.dataset.confirmMessage || defaults.message,
      buttonText: trigger.dataset.confirmButton || defaults.buttonText,
      buttonClass: trigger.dataset.confirmButtonClass || defaults.buttonClass,
      iconClass: trigger.dataset.confirmIconClass || defaults.iconClass,
      iconSvg: trigger.dataset.confirmIconSvg || defaults.iconSvg
    };

    titleEl.textContent = config.title;
    messageEl.textContent = config.message;
    submitBtn.textContent = config.buttonText;
    submitBtn.className = `flex-1 px-4 py-2.5 rounded-lg text-white font-medium transition text-sm ${config.buttonClass}`;
    iconEl.className = `mx-auto h-12 w-12 rounded-full flex items-center justify-center mb-4 ${config.iconClass}`;
    iconEl.innerHTML = config.iconSvg;

    modal.classList.remove("hidden");
    modal.classList.add("flex");

    // Store the trigger info on the modal element for use in confirm/cancel
    modal.dataset.confirmActionHref = trigger.href;
    modal.dataset.confirmActionMethod = trigger.dataset.confirmMethod || "";
  }
});

// Handle cancel and backdrop clicks
document.addEventListener("click", (event) => {
  const modal = document.getElementById("confirm-modal");
  if (!modal) return;

  if (event.target.id === "confirm-cancel") {
    modal.classList.add("hidden");
    modal.classList.remove("flex");
    return;
  }

  // Backdrop click (click on modal itself)
  if (event.target === modal) {
    modal.classList.add("hidden");
    modal.classList.remove("flex");
  }
});

// Handle confirm button click
document.addEventListener("click", (event) => {
  if (event.target.id !== "confirm-submit") return;

  const modal = document.getElementById("confirm-modal");
  if (!modal) return;

  const href = modal.dataset.confirmActionHref;
  const method = modal.dataset.confirmActionMethod;

  if (href) {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = href;
    form.style.display = "none";

    if (method && method.toLowerCase() === "delete") {
      const methodInput = document.createElement("input");
      methodInput.type = "hidden";
      methodInput.name = "_method";
      methodInput.value = "delete";
      form.appendChild(methodInput);
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (csrfToken) {
      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "authenticity_token";
      csrfInput.value = csrfToken.content;
      form.appendChild(csrfInput);
    }

    document.body.appendChild(form);
    form.submit();
  }

  modal.classList.add("hidden");
  modal.classList.remove("flex");
});


// After a Turbo page load, if the URL has a hash, scroll to that element.
// Turbo's body-swap doesn't trigger native anchor scrolling.
document.addEventListener("turbo:load", () => {
  const hash = window.location.hash;
  if (!hash) return;

  let el = null;
  try {
    el = document.querySelector(hash);
  } catch (e) {
    return;
  }
  if (!el) return;

  requestAnimationFrame(() => {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  });
});

// Also handle same-page hash changes (e.g. user clicks a link with a hash on the current page)
document.addEventListener("turbo:click", (event) => {
  const link = event.target.closest("a[href*='#']");
  if (!link) return;

  const href = link.getAttribute("href") || "";
  const hashIndex = href.indexOf("#");
  if (hashIndex === -1) return;

  const path = href.slice(0, hashIndex);
  const hash = href.slice(hashIndex + 1);
  const currentPath = window.location.pathname + window.location.search;

  // Same-page hash navigation — intercept and scroll manually
  if (path === "" || path === currentPath) {
    const el = document.getElementById(hash);
    if (el) {
      event.preventDefault();
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      history.replaceState(null, "", "#" + hash);
    }
  }
});


// --- Password visibility toggle -------------------------------------------
document.addEventListener("click", (event) => {
  const btn = event.target.closest("[data-toggle-password]");
  if (!btn) return;
  event.preventDefault();
  const input = document.getElementById(btn.dataset.togglePassword);
  if (!input) return;
  const isPassword = input.type === "password";
  input.type = isPassword ? "text" : "password";
  const eye = btn.querySelector("[data-icon-eye]");
  const eyeOff = btn.querySelector("[data-icon-eye-off]");
  if (eye) eye.classList.toggle("hidden", !isPassword);
  if (eyeOff) eyeOff.classList.toggle("hidden", isPassword);
});

// --- Password strength checklist ------------------------------------------
function updatePasswordRules(input) {
  const container = document.querySelector(`[data-password-rules][data-password-input="${input.id}"]`);
  if (!container) return;
  const v = input.value || "";
  const rules = {
    length:  v.length >= 8,
    upper:   /[A-Z]/.test(v),
    lower:   /[a-z]/.test(v),
    digit:   /\d/.test(v),
    special: /[^A-Za-z0-9]/.test(v)
  };
  container.querySelectorAll("[data-rule]").forEach((li) => {
    const ok   = rules[li.dataset.rule];
    const icon = li.querySelector("[data-rule-icon]");
    li.classList.toggle("text-green-600", ok);
    li.classList.toggle("text-slate-400", !ok);
    if (!icon) return;
    if (ok) {
      icon.className = "w-4 h-4 shrink-0";
      icon.innerHTML = '<svg class="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>';
    } else {
      icon.className = "w-4 h-4 rounded-full border border-slate-300 flex items-center justify-center shrink-0";
      icon.innerHTML = "";
    }
  });
}

document.addEventListener("input", (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  if (document.querySelector(`[data-password-rules][data-password-input="${input.id}"]`)) {
    updatePasswordRules(input);
  }
});

document.addEventListener("turbo:load", () => {
  document.querySelectorAll("[data-password-rules]").forEach((container) => {
    const input = document.getElementById(container.dataset.passwordInput);
    if (input) updatePasswordRules(input);
  });
});
